import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { BlockerFunction } from 'react-router';
import { useBlocker, useNavigate } from '../compat/router-shim';
import {
  checkNavigationBlockersAtom,
  hasNavigationBlockersAtom,
  hasUnloadWarningAtom,
  parkPendingNavigationAtom,
} from '../store/navigation/navigationBlockerAtom';

/**
 * react-router stamps every history entry it creates with its position (`idx`)
 * in history.state, and measures a POP from it. Null for an entry the router
 * did not create.
 */
const readHistoryIndex = (): number | null => {
  const index: unknown = window.history.state?.idx;
  return typeof index === 'number' ? index : null;
};

/**
 * Enforces the registered navigation blockers at the ROUTER, not at individual
 * buttons (#10668).
 *
 * `navigationBlockerAtom` was only ever consulted by Layout's Back handler, so
 * a screen guarding unsaved work was still bypassed by the browser/OS back
 * gesture, every Drawer link, any <Link>, and each screen's own navigate()
 * calls. Mounting this once above the <Outlet/> routes all of them through the
 * same blockers.
 *
 * The router is NEVER left sitting in `blocked` state. When a blocker objects,
 * the attempted destination is parked and the blocker is reset immediately, so
 * a dialog that closes without resolving anything — Escape, a backdrop tap, or
 * OperatingHoursScreen's do-nothing "continue editing" — cannot wedge
 * navigation. Holding the state open instead made react-router swallow a repeat
 * attempt at the SAME history entry without even consulting the blockers, so
 * Back stopped working once the inspector had fixed the underlying problem.
 *
 * "Leave anyway" re-issues the parked destination behind a one-shot bypass,
 * which is what lets a screen proceed while its own blocker is still
 * registered (the pending value clears a render later).
 *
 * Hard reloads and tab closes are outside the router's reach, so blockers that
 * guard losable data (`warnOnUnload`) additionally arm a `beforeunload` prompt.
 * The browser owns that dialog and ignores custom text.
 */
export const useNavigationGuard = () => {
  const hasNavigationBlockers = useAtomValue(hasNavigationBlockersAtom);
  const hasUnloadWarning = useAtomValue(hasUnloadWarningAtom);
  const checkNavigationBlockers = useSetAtom(checkNavigationBlockersAtom);
  const parkPendingNavigation = useSetAtom(parkPendingNavigationAtom);
  const navigate = useNavigate();

  /** Set for exactly one navigation, so "leave anyway" is not re-blocked. */
  const bypassOnceRef = useRef(false);
  /** How the blocked attempt was made, so resuming keeps Back feeling like Back. */
  const historyActionRef = useRef<string>('PUSH');
  /**
   * The entry a POP was heading for. The browser has already moved onto it when
   * the blocker is consulted and the router steps it straight back, so this is
   * the only moment it can be read.
   */
  const popTargetIndexRef = useRef<number | null>(null);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ historyAction, nextLocation }) => {
      if (bypassOnceRef.current) {
        bypassOnceRef.current = false;
        return false;
      }
      historyActionRef.current = historyAction;
      // The idx is only this router's while the browser shows the entry being
      // moved to (one stored without a key is its 'default'). A memory router
      // leaves window.history to whatever was there before.
      const showingTarget =
        (window.history.state?.key ?? 'default') === nextLocation.key;
      popTargetIndexRef.current = showingTarget ? readHistoryIndex() : null;
      return hasNavigationBlockers;
    },
    [hasNavigationBlockers],
  );

  const blocker = useBlocker(shouldBlock);

  // useBlocker hands back the object held in router state, so its identity is
  // stable across unrelated re-renders and this effect only re-runs on a real
  // state transition — a blocker that setStates (or awaits Dexie) mid-decision
  // cannot abandon its own check.
  const decidedLocationRef = useRef<string | null>(null);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      decidedLocationRef.current = null;
      return;
    }

    const locationKey =
      blocker.location.key ?? blocker.location.pathname ?? 'blocked';
    if (decidedLocationRef.current === locationKey) return;
    decidedLocationRef.current = locationKey;

    const target = blocker.location;
    const wasPop = historyActionRef.current === 'POP';
    const wasReplace = historyActionRef.current === 'REPLACE';
    const popTargetIndex = popTargetIndexRef.current;

    let abandoned = false;

    void (async () => {
      const canNavigate = await checkNavigationBlockers();
      if (abandoned) return;

      if (canNavigate) {
        blocker.proceed();
        return;
      }

      const resume = () => {
        bypassOnceRef.current = true;

        // The router has stepped the browser back onto the entry the attempt
        // started from, so the distance to the captured target replays the
        // original move: -1 for Back, +1 for Forward, more for a long-press
        // on either. A zero distance would reload the page, so an unknown or
        // zero one falls through to navigating to the target, which still
        // lands on the right screen.
        const currentIndex = readHistoryIndex();
        const popDelta =
          wasPop && popTargetIndex !== null && currentIndex !== null
            ? popTargetIndex - currentIndex
            : 0;

        if (popDelta !== 0) {
          // history.go() is asynchronous, so the router only consults the
          // blocker once popstate lands — the bypass has to stay armed until
          // then. The router registers its own popstate handler when it
          // initialises, so this one always runs after it has decided.
          const disarm = () => {
            bypassOnceRef.current = false;
            window.removeEventListener('popstate', disarm);
          };
          window.addEventListener('popstate', disarm);
          void navigate(popDelta);
          return;
        }

        void navigate(
          {
            pathname: target.pathname,
            search: target.search,
            hash: target.hash,
          },
          { replace: wasReplace, state: target.state },
        );
        // A push/replace runs the blocker synchronously inside navigate(), so
        // the bypass has been consumed by the time this line is reached.
        // Clearing it explicitly keeps a navigation the router declined to
        // start (an unmounted caller, an identical target) from leaving the
        // flag armed for the next, unrelated attempt.
        bypassOnceRef.current = false;
      };

      // A blocker objected and has raised its own dialog. Park the destination
      // and release the router straight away — see the note above on why the
      // blocked state must not be held open.
      parkPendingNavigation({
        proceed: () => {
          // The router steps a blocked POP back with its own history.go(),
          // which lands asynchronously. Until it has, the browser is still on
          // the target, so "leave anyway" pressed that quickly waits for it
          // rather than measuring a distance of zero from there.
          if (
            wasPop &&
            popTargetIndex !== null &&
            readHistoryIndex() === popTargetIndex
          ) {
            const resumeOnceSteppedBack = () => {
              window.removeEventListener('popstate', resumeOnceSteppedBack);
              resume();
            };
            window.addEventListener('popstate', resumeOnceSteppedBack);
            return;
          }

          resume();
        },
        // Nothing to undo: the router was released when the block was raised.
        reset: () => {},
      });
      blocker.reset();
    })();

    return () => {
      abandoned = true;
    };
  }, [blocker, checkNavigationBlockers, parkPendingNavigation, navigate]);

  // Once nothing is guarding the screen any more there is no attempt left to
  // resume, so drop the parked destination rather than let a later
  // "leave anyway" fire it.
  useEffect(() => {
    if (!hasNavigationBlockers) parkPendingNavigation(null);
  }, [hasNavigationBlockers, parkPendingNavigation]);

  useEffect(() => {
    if (!hasUnloadWarning) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = 'Changes you made may not be saved.';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnloadWarning]);
};
